import { Film } from '../services';
import { IntlShape } from 'react-intl';

/* A CMS description that already names the brand was written by hand for
   search and is used as is — same rule as the title's. */
const BRAND_PATTERN = /muvi|موڤي|موفي/i;

/* Google renders roughly this much of a description; the rest is dropped. The
   suffix is what must survive, so the synopsis is trimmed to leave room. */
const MAX_LENGTH = 160;

/**
 * The movie page's meta description.
 *
 * The CMS `descriptionTag` is the bare plot synopsis for every film, so the
 * description named no place at all. A search like "digger in riyadh" then had
 * no geography to match anywhere on the page, while VOX's equivalent carries
 * "Watch the latest movies in Saudi Arabia with VOX Cinemas" — and VOX takes
 * the AI Overview and position 1 for that query.
 *
 * The suffix names the country, not a city. Cities would be stronger, but we
 * cannot state them truthfully here: `movieLocations` is empty for any film
 * without advanced booking, and even when populated it holds only the cookie
 * city, not the full list of cities showing the film. The country is accurate
 * for every film in every state.
 * @param {Film} film - the film being rendered
 * @param {IntlShape} intl - for the localized suffix
 * @returns The description, synopsis first, then the booking-and-country line.
 */
export const buildMovieSeoDescription = (film: Film, intl: IntlShape): string => {
  const suffix = intl.formatMessage({ id: 'seo.movie.description_suffix' });
  const synopsis = (film?.descriptionTag || film?.synopsis || '').trim();

  if (!synopsis) return suffix;
  if (BRAND_PATTERN.test(synopsis)) return synopsis;

  /* Trim at a word boundary so the synopsis does not end mid-word. */
  const room = MAX_LENGTH - suffix.length - 1;
  const trimmed =
    synopsis.length <= room
      ? synopsis
      : `${synopsis.slice(0, synopsis.lastIndexOf(' ', room)).replace(/[.,;:]$/, '')}…`;

  return `${trimmed} ${suffix}`;
};
